import { useState, useEffect } from 'react';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import LoadingSpinner from '@/shared/ui/LoadingSpinner.jsx';
import { useTranslation } from 'react-i18next';
import { useGetRequest, usePostRequest, useDeleteRequest } from '@/shared/hooks/requests.js';
import { FILE_COMMENTS, COMMENT_DELETE } from '@/shared/utils/urls.js';
import { MessageSquare, Send, Trash2, User } from 'lucide-react';
import { formatDate } from '@/shared/utils/helpers.js';

export const CommentsDrawer = ({ open, onOpenChange, file }) => {
    const { t } = useTranslation();
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');

    const loadComments = useGetRequest({
        url: FILE_COMMENTS.replace('{id}', file?.id)
    });

    const addComment = usePostRequest({
        url: FILE_COMMENTS.replace('{id}', file?.id)
    });

    const deleteComment = useDeleteRequest({});

    const fetchComments = () => {
        if (file?.id) {
            loadComments.request().then(({ success, response }) => {
                if (success) {
                    // Handle both direct array response and object with comments property
                    const data = Array.isArray(response) ? response : (response.comments || []);
                    setComments(data);
                }
            });
        }
    };

    useEffect(() => {
        if (open) {
            fetchComments();
        }
    }, [open, file?.id]);

    const handleAddComment = async () => {
        if (!newComment.trim()) return;

        const { success } = await addComment.request({
            data: { content: newComment.trim() }
        });

        if (success) {
            setNewComment('');
            fetchComments();
        }
    };

    const handleDelete = async (commentId) => {
        const url = COMMENT_DELETE.replace('{id}', commentId);
        deleteComment.setUrl(url);

        const { success } = await deleteComment.request();
        if (success) {
            fetchComments();
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="w-full sm:w-[540px] flex flex-col h-full">
                <SheetHeader>
                    <SheetTitle>
                        <div className="flex items-center gap-2">
                            <MessageSquare className="h-5 w-5" />
                            {t('my-docs:comments.title')}
                        </div>
                    </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto py-6 px-6">
                    {loadComments.loading ? (
                        <div className="flex justify-center py-8">
                            <LoadingSpinner />
                        </div>
                    ) : comments.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            {t('my-docs:comments.noComments')}
                        </div>
                    ) : (
                        <div className="space-y-6 pb-4">
                            {comments.map((comment) => {
                                const userName = comment.user?.fullName || comment.user?.full_name || t('my-docs:comments.unknownUser');
                                const initials = userName
                                    .split(' ')
                                    .map(n => n[0])
                                    .join('')
                                    .toUpperCase()
                                    .substring(0, 2);

                                return (
                                    <div key={comment.id} className="group relative flex gap-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                        {/* Avatar */}
                                        <div className="flex-shrink-0">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand to-[#324E8C] flex items-center justify-center text-white text-xs font-bold shadow-md border-2 border-background">
                                                {initials || <User className="h-5 w-5" />}
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-sm text-foreground">
                                                        {userName}
                                                    </span>
                                                    <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                                        {formatDate(comment.createdAt || comment.created_at)}
                                                    </span>
                                                </div>

                                                {(comment.isOwner || comment.is_owner) && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 opacity-0 max-md:opacity-100 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-500 hover:bg-red-50 rounded-full"
                                                        onClick={() => handleDelete(comment.id)}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                            </div>

                                            <div className="bg-muted/30 group-hover:bg-muted/50 transition-colors rounded-md rounded-tl-none p-3 border border-transparent group-hover:border-border/50">
                                                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words">
                                                    {comment.content}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t bg-card/50 backdrop-blur-md">
                    <div className="relative flex flex-col gap-2 bg-background border rounded-md p-2 transition-all focus-within:border-brand focus-within:ring-1 focus-within:ring-brand/20 shadow-sm">
                        <Textarea
                            placeholder={t('my-docs:comments.placeholder')}
                            value={newComment}
                            maxLength={10000}
                            onChange={(e) => setNewComment(e.target.value)}
                            className="resize-none min-h-[44px] max-h-[160px] border-0 focus-visible:ring-0 bg-transparent text-[14px] leading-relaxed py-1 px-2"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleAddComment();
                                }
                            }}
                        />
                        <div className="flex items-center justify-between border-t pt-2 px-1">
                            <div className="text-[10px] text-muted-foreground">
                                {newComment.length > 0 && `${newComment.length} ${t('common:characters')}`}
                            </div>
                            <Button
                                size="sm"
                                className="h-8 px-4 bg-brand hover:bg-[#324E8C] text-white rounded-md transition-all shadow-sm active:scale-95 disabled:opacity-50"
                                onClick={handleAddComment}
                                disabled={!newComment.trim() || addComment.loading}
                            >
                                {addComment.loading ? (
                                    <LoadingSpinner color="#fff" size={14} />
                                ) : (
                                    <div className="flex items-center gap-1.5 font-medium text-xs">
                                        <span>{t('common:send')}</span>
                                        <Send className="h-3.5 w-3.5" />
                                    </div>
                                )}
                            </Button>
                        </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground text-center mt-3 px-4">
                        {t('my-docs:comments.hint', { defaultValue: 'Press Enter to send, Shift + Enter for new line' })}
                    </p>
                </div>
            </SheetContent>
        </Sheet>
    );
};
